/* tslint:disable */
/* eslint-disable */

import * as FirstCommonTypes from "first-common/javaTypes";

export interface BasicAllianceDescription extends AllianceDescription {
    alliance: number;
}

export interface DoubleEliminationSeriesDescription extends SeriesDescription {
    bracketHalf: BracketHalf;
    optional: boolean;
}

export interface PlayoffStructure<SD> {
    type: PlayoffType;
    version: number;
    allianceCount: number;
    series: SD[];
}

export interface RoundRobinAllianceDescription extends AllianceDescription {
    rank: number;
}

export interface SeriesAllianceDescription extends AllianceDescription {
    series: number;
    state: SeriesAllianceState;
}

export interface SeriesDescription {
    series: number;
    name: string;
    shortName: string;
    matchesToWin: number;
    round: number;
    red: AllianceDescription;
    blue: AllianceDescription;
    isFinals: boolean;
}

export interface InspectionUpdate {
    forms: FormType[];
    statuses: InspectionUpdateStatus[];
    teams: InspectionUpdateTeam[];
    readyQueues: Record<string, number[]>;
}

export interface AdvancementConfig {
    advancementCount: number;
    reserveCount: number;
    advancesTo: string;
    advanceHost: boolean;
    host: number;
    hostName: string;
    canAdvanceHost: boolean;
}

export interface Award {
    awardId: string;
    awardTypeId: AwardId;
    awardSubtypeId: number;
    order: number;
    displayOrder: number;
    allowManualEntry: boolean;
    editable: boolean;
    isTeamAward: boolean;
    name: string;
    script: string;
    requiredCount: number;
}

export interface AwardAssignment {
    series: number;
    isPublic: boolean;
    team?: AwardTeam;
    name?: string;
    comment?: string;
}

export interface AwardPresentationContext {
    divisions: Record<string, string>;
    allianceCount: number;
    teamCount: number;
    presentationGroups: AwardId[][];
    advancementConfig?: AdvancementConfig;
    advancements: CommonAdvancement[];
    scriptCustomizations: ScriptCustomization[];
    areTeamAwardsSubmitted: boolean;
    isSyncConfigured: boolean;
    isRcmp: boolean;
    globalAwards: boolean;
}

export interface CenterStageScoreDetail extends ScoreDetail {
    fieldColor?: AllianceColor;
    initTeamProp1: boolean;
    initTeamProp2: boolean;
    robot1Auto: boolean;
    robot2Auto: boolean;
    spikeMarkPixel1: boolean;
    spikeMarkPixel2: boolean;
    targetBackdropPixel1: boolean;
    targetBackdropPixel2: boolean;
    autoBackdrop: number;
    autoBackstage: number;
    dcBackdrop: number;
    dcBackstage: number;
    mosaics: number;
    maxSetLine: number;
    egRobot1: EndGameLocation;
    egRobot2: EndGameLocation;
    drone1: number;
    drone2: number;
    minorPenalties: number;
    majorPenalties: number;
    autoNavigatingPoints: number;
    autoRandomizationPoints: number;
    autoBackdropPoints: number;
    autoBackstagePoints: number;
    dcBackdropPoints: number;
    dcBackstagePoints: number;
    mosaicPoints: number;
    setBonusPoints: number;
    egLocationPoints: number;
    egDronePoints: number;
    autoPoints: number;
    dcPoints: number;
    endgamePoints: number;
    penaltyPointsCommitted: number;
    prePenaltyTotal: number;
}

export interface CommonAdvancement {
    criteria: string;
    team: number;
    displayTeam: string;
    teamName: string;
    status: AdvancementStatus;
    points?: CommonAdvancementRank;
}

export interface CommonEventParticipant {
    team: TeamData;
    division: number;
    inspireEligible: boolean;
    competingType: CompetingType;
    advancementEligibility: AdvancementEligibility;
}

export interface DecodeScoreDetail extends ScoreDetail {
    autoClassifiedArtifacts: number;
    autoOverflowArtifacts: number;
    autoClassifierState: ArtifactType[];
    robot1Auto: boolean;
    robot2Auto: boolean;
    teleopClassifiedArtifacts: number;
    teleopOverflowArtifacts: number;
    teleopDepotArtifacts: number;
    teleopClassifierState: ArtifactType[];
    robot1Teleop: DecodeTeleopBaseLocation;
    robot2Teleop: DecodeTeleopBaseLocation;
    minorFouls: number;
    majorFouls: number;
    autoLeavePoints: number;
    autoArtifactPoints: number;
    autoPatternPoints: number;
    teleopArtifactPoints: number;
    teleopDepotPoints: number;
    teleopPatternPoints: number;
    teleopBasePoints: number;
    autoPoints: number;
    teleopPoints: number;
    foulPointsCommitted: number;
    preFoulTotal: number;
    violations: string[];
    ownMajorFouls: number;
    ownMinorFouls: number;
    otherMajorFouls: number;
    otherMinorFouls: number;
    hrMajorFouls: number;
    hrMinorFouls: number;
    movementRP: boolean;
    goalRP: boolean;
    patternRP: boolean;
}

export interface IntoTheDeepScoreDetail extends ScoreDetail {
    robot1Auto: ItdAutoLocation;
    robot2Auto: ItdAutoLocation;
    autoSampleNet: number;
    autoSampleLow: number;
    autoSampleHigh: number;
    autoSpecimenLow: number;
    autoSpecimenHigh: number;
    teleopSampleNet: number;
    teleopSampleLow: number;
    teleopSampleHigh: number;
    teleopSpecimenLow: number;
    teleopSpecimenHigh: number;
    robot1Teleop: ItdTeleopLocation;
    robot2Teleop: ItdTeleopLocation;
    minorFouls: number;
    majorFouls: number;
    autoSamplePoints: number;
    autoSpecimenPoints: number;
    autoParkPoints: number;
    autoAscentPoints: number;
    teleopSamplePoints: number;
    teleopSpecimenPoints: number;
    teleopParkPoints: number;
    teleopAscentPoints: number;
    autoPoints: number;
    teleopPoints: number;
    foulPointsCommitted: number;
    preFoulTotal: number;
}

export interface PowerPlayScoreDetail extends ScoreDetail {
    fieldColor?: AllianceColor;
    circuitPattern: number;
    sideOfField?: FieldSide;
    initSignalSleeve1: boolean;
    initSignalSleeve2: boolean;
    robot1Auto: AutoNavigation;
    robot2Auto: AutoNavigation;
    autoTerminal: number;
    autoTerminalFar: number;
    autoJunctions: JunctionElement[][][];
    dcJunctions: JunctionElement[][][];
    dcTerminalNear: number;
    dcTerminalFar: number;
    dcTerminalOther: number;
    egNavigated1: boolean;
    egNavigated2: boolean;
    minorPenalties: number;
    majorPenalties: number;
    autoNavigationPoints: number;
    signalBonusPoints: number;
    autoJunctionConePoints: number;
    autoTerminalConePoints: number;
    dcJunctionConePoints: number;
    dcTerminalConePoints: number;
    ownershipPoints: number;
    circuitPoints: number;
    egNavigationPoints: number;
    autoPoints: number;
    dcPoints: number;
    endgamePoints: number;
    penaltyPointsCommitted: number;
    prePenaltyTotal: number;
    autoJunctionCones: number[];
    dcJunctionCones: number[];
    beacons: number;
    ownedJunctions: number;
    circuit: boolean;
}

export interface Release extends Comparable<Release> {
    name: string;
    summary: string;
    details: string;
    uploadIds: string[];
}

export interface Sponsor extends Comparable<Sponsor> {
    sponsorId: string;
    name: string;
    title: string;
    position: number;
    logo: string;
    level: SponsorLevel;
}

export interface TimeoutReference {
    type: TimeoutType;
    timeoutStart: DateAsNumber;
    timeoutDuration: string;
    description: string;
}

export interface AllianceLastMatchTimeout extends TimeoutReference {
    srcMatch: number;
    srcRound: number;
    seed: number;
    alliance: string;
    isFinals: boolean;
}

export interface G301Timeout extends TimeoutReference {
}

export interface LastOnFieldTimeout extends TimeoutReference {
    srcMatch: number;
    srcRound: number;
    field: number;
}

export interface RunningMatchTimeout extends TimeoutReference {
    match: number;
}

export interface ScheduledStartTimeout extends TimeoutReference {
    match: number;
}

export interface T206MatchDependency extends TimeoutReference {
    srcMatch: number;
}

export interface T206Timeout extends TimeoutReference {
    targetMatch: number;
    awaitingScorePost: boolean;
    srcMatch: number;
    teams: number[];
}

export interface DisplayCommand<T> extends Cloneable {
    type: DisplayCommandType;
    params: T;
    ts: number;
    index: number;
    field: number;
    init: boolean;
}

export interface AbortMatch extends MatchInfo<MatchTeam>, DisplayCommandParams {
}

export interface CancelMatch extends DisplayCommandParams {
    number: number;
}

export interface EndTimeout extends TimeoutUpdate {
}

export interface PitUpdate<T> extends DisplayCommandParams {
    message: T;
}

export interface PlayVideo extends DisplayCommandParams {
    videoUrl: string;
    subtitleUrl: string;
}

export interface Randomize extends MatchInfo<MatchTeam>, DisplayCommandParams {
    color?: AllianceColor;
}

export interface Redirect extends DisplayCommandParams {
    displayParams: Record<string, any>;
    field: number;
}

export interface ScoreUpdate extends MatchInfo<MatchResultsTeam>, DisplayCommandParams {
    redScores: ScoreDetail;
    blueScores: ScoreDetail;
    rand: number;
    hrReview: boolean;
}

export interface SetConfig extends DisplayCommandParams {
    config: any;
}

export interface ShowAdvancement extends DisplayCommandParams {
    config: DisplayAdvancementConfig;
    list: CommonAdvancement[];
    index: number;
}

export interface ShowAward extends DisplayCommandParams {
    award: DisplayAward;
    assignments: DisplayAwardAssignment[];
    place: number;
}

export interface ShowBlank extends DisplayCommandParams {
}

export interface ShowBracket extends DisplayCommandParams {
    style: BracketStyle;
}

export interface ShowInspectionStatus extends DisplayCommandParams {
}

export interface ShowKey extends DisplayCommandParams {
    singleTeam: boolean;
}

export interface ShowMatch extends MatchInfo<MatchTeam>, DisplayCommandParams {
}

export interface ShowMessage extends DisplayCommandParams {
    message: string;
}

export interface ShowOnlineResults extends DisplayCommandParams {
    url: string;
}

export interface ShowPreview extends MatchInfo<MatchTeam>, DisplayCommandParams {
    forceFullscreen: boolean;
}

export interface ShowRandom extends MatchInfo<MatchTeam>, DisplayCommandParams {
    color?: AllianceColor;
}

export interface ShowRankings extends DisplayCommandParams {
}

export interface ShowResults extends MatchInfo<MatchResultsTeam>, DisplayCommandParams {
    redScores: ScoreDetail;
    blueScores: ScoreDetail;
    redScore: number;
    blueScore: number;
    winner?: AllianceColor;
    redHighScore: boolean;
    blueHighScore: boolean;
    isEventConclusion: boolean;
    isDivisionConclusion: boolean;
    highScoreLevel: HighScoreLevel;
    skipAnimation: boolean;
}

export interface ShowRoundResults extends DisplayCommandParams {
    round: number;
    results: ShowResults[];
}

export interface ShowSafetySecurity extends DisplayCommandParams {
}

export interface ShowSelection extends DisplayCommandParams {
    state?: SelectionState;
}

export interface ShowSetup extends MatchInfo<MatchTeam>, DisplayCommandParams {
}

export interface ShowSlideshow extends DisplayCommandParams {
}

export interface ShowSponsors extends DisplayCommandParams {
}

export interface ShowTimeout extends TimeoutUpdate {
}

export interface ShowVideo extends DisplayCommandParams {
}

export interface ShowWifi extends DisplayCommandParams {
}

export interface StartMatch extends MatchInfo<MatchTeam>, DisplayCommandParams {
}

export interface TimeoutUpdate extends DisplayCommandParams {
    timeouts: TimeoutReference[];
    lineupsRequired: boolean;
    assumedNext: any[];
}

export interface PitBracketMessage extends PitMessage<GenericBracket> {
    inElims: boolean;
    structure: PlayoffStructure<any>;
}

export interface PitOnlineURLMessage extends PitMessage<string> {
}

export interface PitPlayoffRank {
    rank: number;
    seed: number;
    displaySeed: string;
    team: string[];
    RP: string;
    TBP1: string;
    TBP2: string;
    plays: number;
    wins: number;
    losses: number;
    ties: number;
}

export interface PitRank {
    rank: number;
    leagueRank: number;
    team: string;
    RP: string;
    TBP1: string;
    TBP2: string;
    plays: number;
    wins: number;
    losses: number;
    ties: number;
}

export interface PitRankingMessage<T> extends PitMessage<T> {
}

export interface PlayoffPitResult extends PitResult {
    redSeed: string;
    blueSeed: string;
}

export interface QualsPitResult extends PitResult {
}

export interface PitResultMessage extends PitMessage<PitResult> {
    matchesPerTeam: number;
    lastUpdatedAt: number;
}

export interface FLLConstants {
}

export interface FTCConstants {
}

export interface MatchTiming {
    auto: string;
    transition: string;
    teleop: string;
    endgameWarning: string;
    total: string;
}

export interface ProgramConstants {
    currentSeasonCmpYear: number;
    currentSeasonName: string;
    presentingSponsor: string;
    currentFirstSeasonName: string;
    firstPresentingSponsor: string;
    program: FirstCommonTypes.Program;
}

export interface AllianceDescription {
}

export interface AwardTeam {
    number: number;
    displayNumber: string;
    name: string;
    school: string;
    location: string;
}

export interface ScoreDetail {
    clazz: string;
    adjust: number;
}

export interface CommonAdvancementRank extends BaseRank {
}

export interface TeamData extends Comparable<TeamData> {
    number: number;
    displayNumber: string;
    name: string;
    school?: string;
    city: string;
    state: string;
    country: string;
    rookie: number;
    teamId: string;
    FMSSeasonId: string;
    FMSRegionId: string;
    teamIdUNUSED: number;
    robotName: string;
    website: string;
    wasAddedFromUi: number;
    CMPprequalified: number;
    schoolName: string;
    paid: boolean;
    demoTeam: boolean;
    FMSHomeCMPId: string;
    gameSpecifics: any;
    createdOn: DateAsNumber;
    createdBy: string;
    modifiedOn: DateAsNumber;
    modifiedBy: string;
}

export interface Cloneable {
}

export interface DisplayCommandParams {
}

export interface DisplayAdvancementConfig {
    advancesTo: string;
    advancementCount: number;
    reserveCount: number;
    isExtraRegional: boolean;
}

export interface DisplayAward {
    awardTypeId: number;
    name: string;
    isTeamAward: boolean;
}

export interface SelectionState {
    teams: TeamSelectionState[];
    teamsPerAlliance: number;
    alliances: number[][];
    nextSlot: number;
    immutable: boolean;
    playoffStartTime: string;
    divisionsTogether: boolean;
    activeSelection?: number;
}

export interface PitResult {
    played: boolean;
    match: string;
    red: number;
    blue: number;
    winner: string;
    redTeams: string[];
    blueTeams: string[];
}

export interface FormType {
    abbrev: string;
    code: string;
    name: string;
    hasForm: boolean;
    statusType: StatusType;
}

export interface InspectionUpdateStatus {
    name: Status;
    displayName: string;
    color: string;
    textWhite: boolean;
}

export interface InspectionUpdateTeam {
    number: number;
    displayNumber: string;
    name: string;
    division: number;
    statuses: Record<string, Status>;
}

export interface ScriptCustomization {
    key: string;
    content: string;
    enabled: boolean;
}

export interface BaseRank {
    tuple: number[];
    team: number;
    code: string;
    timestamp: number;
}

export interface Comparable<T> {
}

export interface MatchAlliance<T> {
    teams: T[];
    wins: number;
    losses: number;
    ties: number;
    wonSeries: boolean;
    seed: string;
}

export interface MatchInfo<T> extends DisplayCommandParams {
    number: number;
    series: number;
    displayNumber: number;
    matchName: string;
    field: number;
    tournamentLevel: TournamentLevel;
    red: MatchAlliance<T>;
    blue: MatchAlliance<T>;
    singleTeam: boolean;
}

export interface MatchTeam extends BasicTeam {
    number: string;
    name: string;
    carriesCard: boolean;
    ranking: number;
    leagueRanking: number;
}

export interface MatchResultsTeam extends BasicTeam {
    number: string;
    name: string;
    card: number;
    ranking: number;
    leagueRanking: number;
    rankMove: RankMove;
}

export interface DisplayAwardAssignment {
    place: number;
    teamNumber: string;
    teamName: string;
    teamSchool: string;
    teamLocation: string;
    name: string;
    division: string;
}

export interface PitMessage<T> {
    type: PitMessageType;
    division: number;
    payload: T[];
}

export interface GenericBracket {
    seriesData: BracketLayer[];
}

export interface BasicTeam {
}

export interface TeamSelectionState extends Comparable<TeamSelectionState> {
    number: number;
    displayNumber: string;
    name: string;
    rank: number;
    declined: boolean;
    removed: boolean;
    captain: boolean;
    picked: boolean;
}

export interface BracketLayer {
    series: GenericSeries;
    label: string;
}

export interface GenericSeries {
    red: SeriesAlliance;
    blue: SeriesAlliance;
    ties: number;
}

export interface SeriesAlliance {
    alliance: number;
    seed: string;
    wins: number;
    teams: TeamData[];
}

export type DateAsNumber = number;

export const enum AllianceColor {
    RED = "RED",
    BLUE = "BLUE",
}

export const enum EventTypeKind {
    SCRIMMAGE = "SCRIMMAGE",
    LEAGUE_MEET = "LEAGUE_MEET",
    QUALIFIER = "QUALIFIER",
    LEAGUE_TOURNAMENT = "LEAGUE_TOURNAMENT",
    SUPER_QUALIFIER = "SUPER_QUALIFIER",
    CHAMPIONSHIP = "CHAMPIONSHIP",
    OTHER = "OTHER",
    FIRST_CHAMPIONSHIP = "FIRST_CHAMPIONSHIP",
    PRACTICE_QUALIFIER = "PRACTICE_QUALIFIER",
    INNOVATION = "INNOVATION",
    OFF_SEASON = "OFF_SEASON",
    INTERVIEW = "INTERVIEW",
    KICKOFF = "KICKOFF",
    WORKSHOP = "WORKSHOP",
    DEMO = "DEMO",
    VOLUNTEER = "VOLUNTEER",
    PRACTICE_DAY = "PRACTICE_DAY",
    PREMIER = "PREMIER",
    PILOT = "PILOT",
}

export const enum SelectionMethod {
    CONTROL = "CONTROL",
    EMCEE = "EMCEE",
    SYSTEM = "SYSTEM",
}

export const enum SelectionOp {
    ACCEPT = "ACCEPT",
    DECLINE = "DECLINE",
    REMOVE = "REMOVE",
    CAPTAIN = "CAPTAIN",
    BACKUP = "BACKUP",
}

export const enum HighScoreLevel {
    DIVISION = "DIVISION",
    EVENT = "EVENT",
    REGION = "REGION",
    WORLD = "WORLD",
}

export const enum HrMeetingNoteType {
    REFEREE = "REFEREE",
    DRIVER = "DRIVER",
    CAPTAIN = "CAPTAIN",
    OTHER = "OTHER",
}

export const enum BracketHalf {
    UPPER = "UPPER",
    LOWER = "LOWER",
}

export const enum PlayoffType {
    SINGLE_ELIMINATION = "SINGLE_ELIMINATION",
    DOUBLE_ELIMINATION = "DOUBLE_ELIMINATION",
    DOUBLE_ELIMINATION_WITH_FINALS = "DOUBLE_ELIMINATION_WITH_FINALS",
    ROUND_ROBIN_WITH_FINALS = "ROUND_ROBIN_WITH_FINALS",
}

export const enum SeriesAllianceState {
    WINNER = "WINNER",
    LOSER = "LOSER",
}

export const enum AwardId {
    JUDGES_ID = "JUDGES_ID",
    COMPASS_ID = "COMPASS_ID",
    PROMOTE_ID = "PROMOTE_ID",
    CONTROL_ID = "CONTROL_ID",
    MOTIVATE_ID = "MOTIVATE_ID",
    DESIGN_ID = "DESIGN_ID",
    INNOVATE_ID = "INNOVATE_ID",
    CONNECT_ID = "CONNECT_ID",
    THINK_ID = "THINK_ID",
    DEANSLIST_ID = "DEANSLIST_ID",
    INSPIRE_ID = "INSPIRE_ID",
    FINALIST_ID = "FINALIST_ID",
    WINNER_ID = "WINNER_ID",
    TOP_RANKED = "TOP_RANKED",
    THIRD_PLACE = "THIRD_PLACE",
    FOURTH_PLACE = "FOURTH_PLACE",
    VOLUNTEER_OF_THE_YEAR_ID = "VOLUNTEER_OF_THE_YEAR_ID",
    VOLUNTEER_ID = "VOLUNTEER_ID",
    DIVISION_FINALIST = "DIVISION_FINALIST",
    DIVISION_WINNER = "DIVISION_WINNER",
    CONFERENCE_FINALIST_ID = "CONFERENCE_FINALIST_ID",
    REACH_ID = "REACH_ID",
    SUSTAIN_ID = "SUSTAIN_ID",
    CUSTOM_ID = "CUSTOM_ID",
}

export const enum EndGameLocation {
    NONE = "NONE",
    BACKSTAGE = "BACKSTAGE",
    RIGGING = "RIGGING",
}

export const enum AdvancementStatus {
    FIRST = "FIRST",
    ALREADY_ADVANCING = "ALREADY_ADVANCING",
    ADVANCING_ABOVE = "ADVANCING_ABOVE",
    INELIGIBLE = "INELIGIBLE",
    NULL = "NULL",
}

export const enum CompetingType {
    FULL = "FULL",
    AWARDS_ONLY = "AWARDS_ONLY",
    NON_COMPETING = "NON_COMPETING",
}

export const enum AdvancementEligibility {
    ELIGIBLE = "ELIGIBLE",
    INELIGIBLE = "INELIGIBLE",
    ALREADY_ADVANCED = "ALREADY_ADVANCED",
}

export const enum DecodeTeleopBaseLocation {
    NONE = "NONE",
    PARTIAL = "PARTIAL",
    FULL = "FULL",
}

export const enum ItdAutoLocation {
    NONE = "NONE",
    OBSERVATION_ZONE = "OBSERVATION_ZONE",
    ASCENT = "ASCENT",
}

export const enum ItdTeleopLocation {
    NONE = "NONE",
    OBSERVATION_ZONE = "OBSERVATION_ZONE",
    ASCENT_1 = "ASCENT_1",
    ASCENT_2 = "ASCENT_2",
    ASCENT_3 = "ASCENT_3",
}

export const enum FieldSide {
    SCORING_SIDE = "SCORING_SIDE",
    AUDIENCE_SIDE = "AUDIENCE_SIDE",
}

export const enum AutoNavigation {
    NONE = "NONE",
    SUBSTATION_TERMINAL = "SUBSTATION_TERMINAL",
    SIGNAL_ZONE = "SIGNAL_ZONE",
}

export const enum SponsorLevel {
    EVENT = "EVENT",
    REGIONAL = "REGIONAL",
    GLOBAL = "GLOBAL",
    DIVISION = "DIVISION",
    DIVISION_PRESENTING = "DIVISION_PRESENTING",
}

export const enum TimeoutType {
    G301 = "G301",
    SCHEDULED_START = "SCHEDULED_START",
    T206 = "T206",
    T206_MATCH_DEPENDENCY = "T206_MATCH_DEPENDENCY",
    LAST_ON_FIELD = "LAST_ON_FIELD",
    ALLIANCE_LAST = "ALLIANCE_LAST",
    RUNNING_MATCH = "RUNNING_MATCH",
}

export const enum DisplayCommandType {
    SHOW_PREVIEW = "SHOW_PREVIEW",
    RANDOMIZE = "RANDOMIZE",
    SHOW_RANDOM = "SHOW_RANDOM",
    SHOW_MATCH = "SHOW_MATCH",
    START_MATCH = "START_MATCH",
    ABORT_MATCH = "ABORT_MATCH",
    CANCEL_MATCH = "CANCEL_MATCH",
    SHOW_RESULTS = "SHOW_RESULTS",
    SHOW_SETUP = "SHOW_SETUP",
    SCORE_UPDATE = "SCORE_UPDATE",
    SHOW_SPONSORS = "SHOW_SPONSORS",
    TIMEOUT_UPDATE = "TIMEOUT_UPDATE",
    SHOW_TIMEOUT = "SHOW_TIMEOUT",
    END_TIMEOUT = "END_TIMEOUT",
    SHOW_SELECTION = "SHOW_SELECTION",
    SHOW_STATUS = "SHOW_STATUS",
    SHOW_AWARD = "SHOW_AWARD",
    SHOW_BRACKET = "SHOW_BRACKET",
    REDIRECT = "REDIRECT",
    SHOW_MESSAGE = "SHOW_MESSAGE",
    SHOW_WIFI = "SHOW_WIFI",
    SHOW_SAFETY_SECURITY = "SHOW_SAFETY_SECURITY",
    SHOW_SLIDESHOW = "SHOW_SLIDESHOW",
    SHOW_BLANK = "SHOW_BLANK",
    SHOW_VIDEO = "SHOW_VIDEO",
    SHOW_KEY = "SHOW_KEY",
    SHOW_RANKINGS = "SHOW_RANKINGS",
    SHOW_ADVANCEMENT = "SHOW_ADVANCEMENT",
    SHOW_ONLINE_RESULTS = "SHOW_ONLINE_RESULTS",
    SHOW_INSPECTION_STATUS = "SHOW_INSPECTION_STATUS",
    PLAY_VIDEO = "PLAY_VIDEO",
    SET_CONFIG = "SET_CONFIG",
    PIT_UPDATE = "PIT_UPDATE",
    SHOW_ROUND_RESULTS = "SHOW_ROUND_RESULTS",
    SHOW_ROTATING_BRACKET = "SHOW_ROTATING_BRACKET",
}

export const enum TournamentLevel {
    QUALIFICATION = "QUALIFICATION",
    SEMIFINAL = "SEMIFINAL",
    FINAL = "FINAL",
    PLAYOFF = "PLAYOFF",
    PRACTICE = "PRACTICE",
    TEST = "TEST",
}

export const enum BracketStyle {
    TWO_LEVEL = "TWO_LEVEL",
    SUB_BRACKET_TOP = "SUB_BRACKET_TOP",
    SUB_BRACKET_BOTTOM = "SUB_BRACKET_BOTTOM",
    FULL = "FULL",
}

export const enum PitMessageType {
    RANKINGS = "RANKINGS",
    ALL_RANKINGS = "ALL_RANKINGS",
    PLAYOFF_RANKINGS = "PLAYOFF_RANKINGS",
    RESULT = "RESULT",
    ONLINE_URL = "ONLINE_URL",
    BRACKET = "BRACKET",
}

export const enum ArtifactType {
    NONE = "NONE",
    PURPLE = "PURPLE",
    GREEN = "GREEN",
}

export const enum JunctionElement {
    MY_CONE = "MY_CONE",
    OTHER_CONE = "OTHER_CONE",
    MY_R1_BEACON = "MY_R1_BEACON",
    MY_R2_BEACON = "MY_R2_BEACON",
    OTHER_R1_BEACON = "OTHER_R1_BEACON",
    OTHER_R2_BEACON = "OTHER_R2_BEACON",
}

export const enum StatusType {
    BASIC = "BASIC",
    BASIC_WITH_TIME = "BASIC_WITH_TIME",
    FULL = "FULL",
}

export const enum Status {
    NONE = "NONE",
    IN_PROGRESS = "IN_PROGRESS",
    FAIL = "FAIL",
    PASS = "PASS",
    READY = "READY",
    LATE = "LATE",
    VERY_LATE = "VERY_LATE",
}

export const enum RankMove {
    NONE = "NONE",
    DOWN = "DOWN",
    UP = "UP",
}
